import { LinksList } from "./LinksList";
import {
  createGeneralCategoryLinks,
  masculineCategoryLinks,
  feminineCategoryLinks,
  childrenCategoryLinks,
} from "./content.mock";

import { PRODUCT_GROUPS, BASE_SEARCH_PRODUCTS_URL } from "@/constants/pages";
import { useThemes } from "@/hooks/use-themes";

import type { JSX } from "react";
import type { SubTheme } from "@/api/theme/models";
import type { LinkGroup, MainLink } from "@/types/categoryFilters";
import type { MobileTopic } from "../mobile/types";

interface Props {
  theme: string;
}

/** Título de coluna do flyout: pequeno, caixa-alta, com respiro para a lista. */
const sectionTitleClassName =
  "mb-1 border-b border-solid border-base-border pb-1 text-brand-primary lg:mb-1 lg:border-none lg:pb-0 lg:text-xs lg:font-bold lg:uppercase lg:tracking-wider";

const addPublicToGeneralLinks = (generalLinks: LinkGroup[], categoryFilter: string): LinkGroup[] => {
  return generalLinks.map((linkGroup) => ({
    ...linkGroup,
    url: `public=${categoryFilter}&${linkGroup.url}`,
    items: linkGroup.items.map((item) => ({
      ...item,
      url: `public=${categoryFilter}&${item.url}`,
    })),
  }));
};

const getCategoryLinksForTheme = (theme: string, licensedThemes: SubTheme[] = []): LinkGroup[] => {
  // public = FEMININO, MASCULINO, INFANTIL
  const categoryFilter = theme.toUpperCase();
  const generalLinks = createGeneralCategoryLinks(licensedThemes);
  const modifiedGeneralLinks = addPublicToGeneralLinks(generalLinks, categoryFilter);

  switch (theme) {
    case "masculino":
      return masculineCategoryLinks.concat(modifiedGeneralLinks);
    case "feminino":
      return feminineCategoryLinks.concat(modifiedGeneralLinks);
    case "infantil":
      return childrenCategoryLinks.concat(modifiedGeneralLinks);
    default:
      return generalLinks;
  }
};

/**
 * Links de destaque + grupos de categorias de um público (masculino/feminino/infantil).
 * Compartilhado entre o flyout desktop e o menu mobile.
 */
const useMainThemeLinks = (theme: string) => {
  const { featuredTheme, licensedThemes } = useThemes({
    shouldFetchFeatured: true,
    shouldFetchLicensed: true,
  });

  const mainLinks: MainLink[] = PRODUCT_GROUPS.slice(-3).map(({ pathname, pageName }) => ({
    name: pageName,
    url: `${pathname}&public=${theme.toUpperCase()}`,
  }));

  if (featuredTheme) {
    mainLinks.push({
      name: featuredTheme.name,
      url: `${BASE_SEARCH_PRODUCTS_URL}?themes=${featuredTheme.id}&public=${theme.toUpperCase()}`,
    });
  }

  return { mainLinks, categoryLinksForTheme: getCategoryLinksForTheme(theme, licensedThemes) };
};

/**
 * Tópicos (accordion) do menu mobile para um público, com "Ver tudo →" ao final de cada grupo.
 * @param theme - "masculino" | "feminino" | "infantil"
 */
export const useMainThemeTopics = (theme: string): MobileTopic[] => {
  const { mainLinks, categoryLinksForTheme } = useMainThemeLinks(theme);

  return [
    { id: "destaques", name: "Destaques", links: mainLinks },
    ...categoryLinksForTheme.map((linkGroup) => ({
      id: linkGroup.name,
      name: linkGroup.name,
      links: [
        ...linkGroup.items.map((link) => ({ name: link.name, url: `${BASE_SEARCH_PRODUCTS_URL}?${link.url}` })),
        { name: "Ver tudo →", url: `${BASE_SEARCH_PRODUCTS_URL}?${linkGroup.url}` },
      ],
    })),
  ];
};

export const MainThemesLayout = ({ theme }: Props): JSX.Element => {
  const { mainLinks, categoryLinksForTheme } = useMainThemeLinks(theme);

  return (
    <div className="mt-2 flex w-full flex-col lg:mt-0 lg:flex-row lg:gap-6">
      <div className="flex w-full flex-col">
        <b className={sectionTitleClassName}>Destaques</b>

        <LinksList plain links={mainLinks} />
      </div>

      {categoryLinksForTheme.map((linkGroup) => (
        <div key={linkGroup.name} className="mt-3 flex w-full flex-col lg:mt-0">
          <b className={sectionTitleClassName}>{linkGroup.name}</b>

          <LinksList
            plain
            links={[
              ...linkGroup.items.map((link) => ({
                ...link,
                url: `${BASE_SEARCH_PRODUCTS_URL}?${link.url}`,
              })),
              {
                url: `${BASE_SEARCH_PRODUCTS_URL}?${linkGroup.url}`,
                name: "Ver tudo →",
                className: "font-bold text-brand-accent",
              },
            ]}
          />
        </div>
      ))}
    </div>
  );
};
